import { attributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { IdsConstructor } from '../../core/ids-element';
import { EventsMixinInterface } from '../ids-events-mixin/ids-events-mixin';
import { LocaleMixinInterface } from '../ids-locale-mixin/ids-locale-mixin';
import { IdsInputInterface } from '../../components/ids-input/ids-input-attributes';
import type IdsTooltip from '../../components/ids-tooltip/ids-tooltip';

export interface ErrorTrackerHandler {
  onErrorTrackerChange?(enabled: boolean): void;
}

/** Interface for elements that support hideErrorMessage (validation mixin) */
type IdsValidateInput = IdsInputInterface & HTMLElement & {
  hideErrorMessage(toHide: boolean): void;
};

type Constraints = IdsConstructor<EventsMixinInterface & ErrorTrackerHandler & LocaleMixinInterface>;

/**
 * Shows an error indicator icon (red triangle) when validation errors occur,
 * instead of displaying the error message below the field.
 * The error message is shown as a tooltip on hover.
 * @param {T} superclass Accepts a superclass and creates a new subclass from it
 * @returns {T} The extended object
 */
const IdsErrorTrackerMixin = <T extends Constraints>(superclass: T) => class extends superclass {
  constructor(...args: any[]) {
    super(...args);
  }

  connectedCallback() {
    super.connectedCallback?.();
    // Defer setup to ensure inner components (e.g. ids-trigger-field) are rendered
    requestAnimationFrame(() => {
      this.#handleErrorTracker();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#detachValidateListener();
    this.#detachLanguageListener();
    this.#removeErrorTrackerIcon();
    this.#removeErrorTrackerTooltip();
  }

  static get attributes() {
    return [
      ...(superclass as any).attributes,
      attributes.ERROR_TRACKER
    ];
  }

  /** The current error message text for tooltip display */
  #errorTrackerMessage = '';

  /** The container element holding the error icon */
  #errorTrackerContainer?: HTMLElement | SVGElement | null;

  /** Cached reference to the error tracker tooltip element */
  #errorTrackerTooltip: IdsTooltip | null = null;

  /**
   * Set the error tracking feature
   * @param {boolean|string} value If true will set `error-tracker` attribute
   */
  set errorTracker(value: boolean | string) {
    const val = stringToBool(value);
    if (val === this.errorTracker) return;

    if (val) {
      this.setAttribute(attributes.ERROR_TRACKER, val.toString());
    } else {
      this.removeAttribute(attributes.ERROR_TRACKER);
    }

    // Forward attribute to inner input for delegated components (e.g. ids-trigger-field)
    if (this.#isDelegated) {
      const innerInput = (this as IdsInputInterface).input as HTMLElement | undefined;
      if (val) {
        innerInput?.setAttribute(attributes.ERROR_TRACKER, 'true');
      } else {
        innerInput?.removeAttribute(attributes.ERROR_TRACKER);
      }
    }

    this.#handleErrorTracker();

    if (typeof this.onErrorTrackerChange === 'function') {
      this.onErrorTrackerChange(val);
    }
  }

  get errorTracker(): boolean {
    return stringToBool(this.getAttribute(attributes.ERROR_TRACKER));
  }

  /**
   * Set up or tear down error tracker based on attribute value
   */
  #handleErrorTracker() {
    if (this.errorTracker) {
      this.#attachValidateListener();
      this.#attachLanguageListener();
      this.#hideValidationMessages(true);

      // If there's already a validation error, restore the icon and tooltip
      const root = this.#validationShadowRoot;
      const hasError = root?.querySelector('.validation-message[type="error"]');
      if (hasError) {
        this.#onValidationError();
      }
    } else {
      this.#detachValidateListener();
      this.#detachLanguageListener();
      this.#removeErrorTrackerIcon();
      this.#removeErrorTrackerTooltip();
      this.#hideValidationMessages(false);
    }
  }

  /**
   * Whether the component delegates validation to an inner input element
   * @returns {boolean} true if validation is delegated to inner input
   */
  get #isDelegated(): boolean {
    return ['IDS-DROPDOWN', 'IDS-MULTISELECT'].includes(this.tagName);
  }

  /**
   * Get the validation target element (inner input for dropdown/multiselect, or self)
   * @returns {HTMLElement} the element that fires validate events
   */
  get #validationTarget(): HTMLElement {
    const thisAsInput = this as IdsInputInterface;
    if (this.#isDelegated && thisAsInput.input) {
      return thisAsInput.input as HTMLElement;
    }
    return this as unknown as HTMLElement;
  }

  /**
   * Get the shadow root that contains validation message elements
   * @returns {ShadowRoot | null | undefined} the shadow root with validation messages
   */
  get #validationShadowRoot(): ShadowRoot | null | undefined {
    const thisAsInput = this as IdsInputInterface;
    if (this.#isDelegated && thisAsInput.input) {
      return (thisAsInput.input as HTMLElement).shadowRoot;
    }
    return this.shadowRoot;
  }

  /**
   * Get the field container element for icon placement and tooltip target
   * @returns {HTMLElement | SVGElement | null | undefined} the field container
   */
  get #fieldContainer(): HTMLElement | SVGElement | null | undefined {
    const thisAsInput = this as IdsInputInterface;
    if (this.#isDelegated) {
      return thisAsInput.input?.fieldContainer;
    }
    return thisAsInput.fieldContainer;
  }

  /**
   * Attach language change listener to update RTL and re-translate error messages
   */
  #attachLanguageListener() {
    this.offEvent('languagechange.errortracker');
    this.onEvent('languagechange.errortracker', this, () => {
      this.#onLanguageChange();
    });
  }

  #detachLanguageListener() {
    this.offEvent('languagechange.errortracker');
  }

  /**
   * Listen for validate events to intercept error messages
   */
  #attachValidateListener() {
    const target = this.#validationTarget;
    this.offEvent('validate.errortracker');
    this.onEvent('validate.errortracker', target, (e: CustomEvent) => {
      if (!this.errorTracker) return;

      const { isValid } = e.detail;
      if (!isValid) {
        this.#onValidationError();
      } else {
        this.#onValidationClear();
      }
    });
  }

  #detachValidateListener() {
    this.offEvent('validate.errortracker');
  }

  /**
   * Called when validation fails — show the error icon and set up tooltip
   */
  #onValidationError() {
    const root = this.#validationShadowRoot;
    const messages = root?.querySelectorAll('.validation-message[type="error"]');
    const messageTexts: string[] = [];

    messages?.forEach((msg: Element) => {
      const messageText = msg.querySelector('.message-text');
      const text = (messageText as HTMLElement & { visibleTextContent?: string })?.visibleTextContent
        ?? messageText?.textContent?.trim();
      if (text) messageTexts.push(text);
    });

    this.#errorTrackerMessage = messageTexts.join(', ');
    this.#hideValidationMessages(true);
    this.#appendErrorTrackerIcon();
    this.#setupErrorTrackerTooltip();
  }

  /**
   * Called when validation passes — remove the error icon and tooltip
   */
  #onValidationClear() {
    this.#errorTrackerMessage = '';
    this.#removeErrorTrackerIcon();
    this.#removeErrorTrackerTooltip();
    this.#hideValidationMessages(false);
  }

  /**
   * Hide or show the standard validation message elements
   * @param {boolean} hide true to hide, false to show
   */
  #hideValidationMessages(hide: boolean) {
    const root = this.#validationShadowRoot;
    const shouldHide = hide && this.errorTracker;

    const messages = root?.querySelectorAll('.validation-message');
    messages?.forEach((msg: Element) => {
      (msg as HTMLElement).toggleAttribute('hidden', shouldHide);
    });

    // Tell the validation mixin to hide future messages
    const target = this.#validationTarget as unknown as Partial<IdsValidateInput>;
    if (typeof target.hideErrorMessage === 'function') {
      target.hideErrorMessage(shouldHide);
    }
  }

  /**
   * Append the error tracker icon (red triangle) to the field container
   */
  #appendErrorTrackerIcon() {
    let icon = this.#errorTrackerContainer?.querySelector('.icon-error-tracker');
    if (!icon) {
      icon = document.createElement('ids-icon');
      icon.setAttribute('icon', 'dirty-filled');
      icon.setAttribute('width', '12');
      icon.setAttribute('height', '12');
      icon.setAttribute('part', 'error-tracker-icon');
      icon.className = 'icon-error-tracker';

      if (this.localeAPI?.isRTL()) icon.setAttribute('dir', 'rtl');

      this.#errorTrackerContainer = this.#fieldContainer ?? null;
      this.#errorTrackerContainer?.prepend(icon);
    }
  }

  /**
   * Remove the error tracker icon if it exists
   */
  #removeErrorTrackerIcon() {
    const icon = this.#errorTrackerContainer?.querySelector('.icon-error-tracker');
    if (icon) {
      icon.remove();
    }
  }

  /**
   * Get a unique tooltip ID scoped to this component instance
   * @returns {string} the tooltip element ID
   */
  get #tooltipId(): string {
    return `error-tracker-tooltip-${this.id || this.tagName.toLowerCase()}`;
  }

  /**
   * Set up a tooltip on the field container to show the error message on hover
   */
  #setupErrorTrackerTooltip() {
    if (!this.#errorTrackerMessage) return;

    const target = this.#fieldContainer;
    if (!target) return;

    target.setAttribute('tooltip', this.#errorTrackerMessage);

    this.offEvent('hoverend.errortracker');
    this.onEvent('hoverend.errortracker', target, () => {
      if (!this.#errorTrackerMessage) return;

      if (!this.#errorTrackerTooltip) {
        const container = this.closest('ids-container') || document.querySelector('ids-container') || document.body;
        this.#errorTrackerTooltip = document.createElement('ids-tooltip') as IdsTooltip;
        this.#errorTrackerTooltip.id = this.#tooltipId;
        container.appendChild(this.#errorTrackerTooltip);
      }

      this.#errorTrackerTooltip.target = target;
      if (this.#errorTrackerTooltip.state) {
        this.#errorTrackerTooltip.state.noAria = true;
      }
      this.#errorTrackerTooltip.textContent = this.#errorTrackerMessage;
      this.#errorTrackerTooltip.visible = true;
      this.#errorTrackerTooltip.popup?.place();
    }, { delay: 500 });
  }

  /**
   * Remove the error tracker tooltip and hover listener
   */
  #removeErrorTrackerTooltip() {
    this.offEvent('hoverend.errortracker');

    const target = this.#fieldContainer;
    target?.removeAttribute('tooltip');

    this.#errorTrackerTooltip?.remove();
    this.#errorTrackerTooltip = null;
  }

  /** Handle language changes for RTL support and re-translate error messages */
  #onLanguageChange() {
    const icon = this.#errorTrackerContainer?.querySelector('.icon-error-tracker');
    if (this.localeAPI?.isRTL()) icon?.setAttribute('dir', 'rtl');
    else icon?.removeAttribute('dir');

    // Re-validate to get translated messages when there's an active error
    if (this.errorTracker && this.#errorTrackerMessage) {
      const target = this.#validationTarget as HTMLElement & { checkValidation?(): void };
      if (typeof target.checkValidation === 'function') {
        target.checkValidation();
      }
    }
  }
};

export default IdsErrorTrackerMixin;
